import * as cartApi from '../../helpers/cartApi';
import AsyncView from '../../helpers/AsyncView';
import { updateReactCart } from '../helpers/utilities';
import { createContext, h } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';

const preactApp = document.querySelector('#pxu-preact-app');

const CartContext = createContext();

const CartContextProvider = ({ children }) => {
  const [cart, setCart] = useState();
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);
  const [miniCartData, setMiniCartData] = useState({});

  useEffect(() => {
    getCart();
    setupMiniCart();

    const func = e => {
      getCart(e.detail.openMiniCart);
    };

    preactApp.addEventListener('updateReactCart', func);

    return () => {
      preactApp.removeEventListener('updateReactCart', func);
    };
  }, []);

  const getCart = openMiniCart => {
    cartApi.get().then(response => {
      updateCart(response, openMiniCart);
    });
  };

  const updateCart = (newCart, openMiniCart) => {
    AsyncView.load('/cart', { view: 'compare-price' })
      .then(({ data }) => {
        const mutatedItems = newCart.items.map(item => {
          if (data.cart[`id-${item.variant_id}`]) {
            item.compare_at_price =
              data.cart[`id-${item.variant_id}`].compare_at_price;
            item.upsell_item = data.cart[`id-${item.variant_id}`].upsell_item;
          } else {
            item.compare_at_price = item.price;
            item.upsell_item = null;
          }
          return item;
        });
        setCart({ ...newCart, items: mutatedItems });
        if (openMiniCart) {
          toggleMiniCart();
        }
      })
      .catch(err => {
        setCart(newCart);
      });
  };

  const addLineItem = async event => {
    return await cartApi
      .add(event.target)
      .then(response => {
        updateReactCart(true);
        document.querySelector('[data-cart-items]').scrollTop = 0;
      })
      .catch(response => _handleCartApiError(response));
  };

  const updateLineItem = async (itemId, quantity) => {
    const updatedCart = await cartApi.change(itemId.toString(), quantity);
    const newItem = updatedCart.items.find(item => item.id === itemId);

    updateCart(updatedCart);
    return newItem;
  };

  const removeLineItem = async itemId => {
    const updatedCart = await cartApi.remove(itemId.toString());
    const newItem = updatedCart.items.find(item => item.id === itemId);

    updateCart(updatedCart);
    return newItem;
  };

  /**
   * Generic error handler for cart api
   * Example: 422 response when trying to add a product whose total stock is already in cart
   *
   * @param {Object} response The response from Shopify
   */
  const _handleCartApiError = response => {
    const error = response;
    alert(error);
  };

  /**
   * Toggle MiniCart visibility
   *
   * @param state - forces a visible (true) or hidden (false) state
   */
  const toggleMiniCart = state => {
    let newMiniCartState = false;

    if (state !== undefined) {
      newMiniCartState = state;
    } else {
      newMiniCartState = !isMiniCartOpen;
    }

    if (window.location.pathname.startsWith('/cart')) {
      setIsMiniCartOpen(false);
    } else {
      setIsMiniCartOpen(newMiniCartState);
    }
  };

  /**
   * Retrieve reuseable data from DOM
   *
   */
  const setupMiniCart = () => {
    const dataEl = document.querySelector('[data-section-id="minicart"]');
    const data = dataEl.getAttribute('data-section-data') || dataEl.innerHTML;
    const cartData = JSON.parse(data);

    setMiniCartData(cartData);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addLineItem,
        updateLineItem,
        removeLineItem,
        isMiniCartOpen,
        toggleMiniCart,
        miniCartData,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

function useCartContext() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartContextProvider');
  }
  return context;
}

export { CartContextProvider, useCartContext };
